/**
 * useLeaflet.js
 * Factory composable — manages the Leaflet map instance lifecycle.
 * Returns { map, activeTileId, switchTile, destroy }.
 *
 * Usage (inside Vue mounted()):
 *   this.leaflet = createLeaflet(this.$refs.mapContainer, options);
 *   const map = this.leaflet.map;
 */
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MAP_DEFAULTS } from '../constants/mapDefaults';
import { createTileLayer } from '../utils/tileManager';

/**
 * @param {HTMLElement} container
 * @param {object}      options
 * @param {Function}    emit      - component's $emit function for map events
 */
export function createLeaflet(container, options = {}, emit = () => {}) {
  const map = L.map(container, {
    center:        options.center  || MAP_DEFAULTS.center,
    zoom:          options.zoom    || MAP_DEFAULTS.zoom,
    minZoom:       MAP_DEFAULTS.minZoom,
    zoomControl:   false,     // we render our own controls overlay
    attributionControl: true,
  });

  // ── Tile layers ────────────────────────────────────────────────────────────
  let activeTileId = 'osm';
  let activeTileLayer = createTileLayer('osm').addTo(map);

  function switchTile(providerId) {
    if (providerId === activeTileId) return;
    map.removeLayer(activeTileLayer);
    activeTileLayer = createTileLayer(providerId).addTo(map);
    activeTileId = providerId;
  }

  // ── Map events ─────────────────────────────────────────────────────────────
  map.on('zoomend', () => emit('zoom-change', { zoom: map.getZoom() }));
  map.on('moveend', () => emit('bounds-change', { bounds: map.getBounds() }));

  emit('map-ready', { map });

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  function destroy() {
    map.off();
    map.remove();
  }

  return { map, activeTileId: () => activeTileId, switchTile, destroy };
}
